/**
 * Portal-wide Plotly defaults (fonts, colors, hover behavior, mode bar).
 *
 * `mergeLayout` and `mergeConfig` deep-merge user overrides over these
 * defaults so every chart picks up the portal look unless it explicitly
 * opts out for a specific property.
 */
import type { Config, Layout } from 'plotly.js';

// Single-family string with no fallback stack — the parity rig flags any
// `font.family` diff, and a fallback stack changes the string it reads even
// though the resolved browser font is identical. Page-level `--font-body` /
// `--font-heading` CSS vars still carry the full fallback stack for DOM.
export const PLOTLY_FONT_FAMILY = "'Gill Sans MT'";

/**
 * Tight desktop chart margins (tighter L/R than Plotly's own defaults).
 * No `pad` — pad adds an inner inset that pushes the drawing area down.
 */
export const DEFAULT_MARGIN: Layout['margin'] = {
  l: 50,
  r: 20,
  t: 85,
  b: 40,
};

/**
 * Mode-bar buttons stripped from every chart. `toImage` is removed pending a
 * font-embedded PNG exporter (add the custom button at the component level
 * once font embedding is wired up).
 */
export const DEFAULT_MODE_BAR_BUTTONS_TO_REMOVE: NonNullable<Config['modeBarButtonsToRemove']> = [
  'zoom2d',
  'pan2d',
  'select2d',
  'lasso2d',
  'zoomIn2d',
  'zoomOut2d',
  'autoScale2d',
  'resetScale2d',
  'toImage',
];

/**
 * Axis LINE styling — the black outline + mirror that frames every portal
 * chart. This is the single home for the `{ showline, linecolor, linewidth,
 * mirror }` block that used to be copy-pasted into ~20 chart builders.
 *
 * `DEFAULT_LAYOUT.xaxis`/`yaxis` already spread these values, so charts that
 * route their layout through `mergeLayout` (every chart does — `PlotlyChart`
 * applies it) inherit the line styling automatically. Use this const only
 * where `mergeAxis` can't reach: secondary axes (`yaxis2`), or layouts that
 * never touch `DEFAULT_LAYOUT`.
 */
export const AXIS_LINE = {
  showline: true,
  linecolor: '#000000',
  linewidth: 1,
  mirror: true,
} as const;

/**
 * Axis GRID styling — gridlines on, in the portal's light-gray. `gridcolor`
 * is NOT part of `DEFAULT_LAYOUT` (charts opt in per-axis), so spread this
 * into any axis that wants gridlines instead of re-typing the literal.
 */
export const AXIS_GRID = {
  showgrid: true,
  gridcolor: '#E0E0E0',
} as const;

/** Portal Plotly layout defaults. */
export const DEFAULT_LAYOUT: Partial<Layout> = {
  font: {
    family: PLOTLY_FONT_FAMILY,
    size: 16,
    color: '#000000',
  },
  paper_bgcolor: '#ffffff',
  plot_bgcolor: '#ffffff',
  showlegend: true,
  // Title 18 / legend 16 match the legacy `setTitleAndLegend` defaults in
  // `core/plotly-helper.js` (`titleDefaults.fontsize=18`, `legendDefaults`
  // resolves to 16 via the unset `layout.font.size`).
  title: {
    font: { size: 18, family: PLOTLY_FONT_FAMILY, color: '#000000' },
    x: 0.5,
    xanchor: 'center',
    y: 0.98,
    yanchor: 'top',
  },
  legend: {
    orientation: 'h',
    x: 0.5,
    xanchor: 'center',
    y: 1.02,
    yanchor: 'bottom',
    font: { size: 16, family: PLOTLY_FONT_FAMILY, color: '#000000' },
  },
  margin: DEFAULT_MARGIN,
  hovermode: 'x unified',
  hoverlabel: {
    bgcolor: 'rgba(255,255,255,0.87)',
    bordercolor: '#666',
    align: 'left',
    font: { size: 12, color: 'black' },
  },
  hoverdistance: 10,
  xaxis: {
    hoverformat: '%d %b %Y',
    showspikes: true,
    spikemode: 'across',
    spikesnap: 'cursor',
    spikedash: 'dot',
    spikecolor: '#9CA3AF',
    spikethickness: 1,
    fixedrange: true,
    showline: true,
    linecolor: '#000000',
    linewidth: 1,
    mirror: true,
    ticks: 'inside',
    ticklen: 8,
    tickwidth: 1,
    tickcolor: '#000000',
  },
  yaxis: {
    fixedrange: true,
    showline: true,
    linecolor: '#000000',
    linewidth: 1,
    mirror: true,
    ticks: 'inside',
    ticklen: 8,
    tickwidth: 1,
    tickcolor: '#000000',
  },
};

export const DEFAULT_CONFIG: Partial<Config> = {
  responsive: true,
  displaylogo: false,
  displayModeBar: true,
  scrollZoom: false,
  doubleClick: false,
  showTips: false,
  staticPlot: false,
  editable: false,
  modeBarButtonsToRemove: DEFAULT_MODE_BAR_BUTTONS_TO_REMOVE,
};

type LayoutAxis = Partial<NonNullable<Layout['xaxis']>>;
type LayoutLegend = Partial<NonNullable<Layout['legend']>>;

function mergeAxis(
  base: LayoutAxis | undefined,
  override: LayoutAxis | undefined,
): LayoutAxis | undefined {
  if (!base && !override) return undefined;
  return { ...(base ?? {}), ...(override ?? {}) };
}

function mergeLegend(
  base: LayoutLegend | undefined,
  override: LayoutLegend | undefined,
): LayoutLegend | undefined {
  if (!base && !override) return undefined;
  const b = base ?? {};
  const o = override ?? {};
  return {
    ...b,
    ...o,
    font: { ...(b.font ?? {}), ...(o.font ?? {}) },
  };
}

/**
 * Deep-merge a user-supplied layout over the portal defaults. Nested objects
 * we care about (`font`, `margin`, `hoverlabel`, `legend`, `xaxis`, `yaxis`)
 * are merged one level deep so callers can override a single property without
 * losing the rest of the default. Anything beyond that nests via plain spread.
 */
export function mergeLayout(override: Partial<Layout> | undefined): Partial<Layout> {
  const o = override ?? {};
  return {
    ...DEFAULT_LAYOUT,
    ...o,
    font: { ...DEFAULT_LAYOUT.font, ...(o.font ?? {}) },
    margin: { ...DEFAULT_LAYOUT.margin, ...(o.margin ?? {}) },
    hoverlabel: {
      ...DEFAULT_LAYOUT.hoverlabel,
      ...(o.hoverlabel ?? {}),
      font: {
        ...DEFAULT_LAYOUT.hoverlabel?.font,
        ...(o.hoverlabel?.font ?? {}),
      },
    },
    legend: mergeLegend(DEFAULT_LAYOUT.legend, o.legend),
    xaxis: mergeAxis(DEFAULT_LAYOUT.xaxis, o.xaxis),
    yaxis: mergeAxis(DEFAULT_LAYOUT.yaxis, o.yaxis),
  };
}

/**
 * Deep-merge a user-supplied config over the portal defaults. The key
 * subtlety is `modeBarButtonsToRemove`: callers pass *additional* buttons to
 * strip, so we union with the defaults rather than replacing them.
 */
export function mergeConfig(override: Partial<Config> | undefined): Partial<Config> {
  const o = override ?? {};
  return {
    ...DEFAULT_CONFIG,
    ...o,
    modeBarButtonsToRemove: Array.from(
      new Set([...DEFAULT_MODE_BAR_BUTTONS_TO_REMOVE, ...(o.modeBarButtonsToRemove ?? [])]),
    ),
  };
}
